<template>
  <!-- Desktop: Inline Content -->
  <div v-if="!isMobile" class="places-history">
    <!-- Header -->
    <div class="history-header">
      <h3 class="history-title">
        <i class="fas fa-history"></i>
        {{ isShowingGlobal ? 'Most Visited Places' : 'Places Visited' }}
      </h3>
      <div class="history-actions" v-if="hasHistory && !isShowingGlobal">
        <button 
          @click="clearHistory" 
          class="clear-btn"
          :disabled="loading"
          title="Clear all history"
        >
          <i class="fas fa-trash-alt"></i>
        </button>
        <button 
          @click="refreshHistory" 
          class="refresh-btn"
          :disabled="loading"
          title="Refresh history"
        >
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
        </button>
      </div>
      <div class="history-actions" v-else-if="hasHistory && isShowingGlobal">
        <button 
          @click="refreshGlobal" 
          class="refresh-btn"
          :disabled="loading"
          title="Refresh global places"
        >
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="history-content">
      <!-- Loading state -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
        <p>Loading your places...</p>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="error-state">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button @click="refreshHistory" class="retry-btn">Try Again</button>
      </div>

      <!-- Empty state -->
      <div v-else-if="!hasHistory" class="empty-state">
        <i class="fas fa-map-marker-alt"></i>
        <p v-if="isShowingGlobal">No popular places found</p>
        <p v-else>No places visited yet</p>
        <span v-if="!isShowingGlobal" class="empty-subtitle">Start exploring the world!</span>
        <span v-else class="empty-subtitle">Be the first to explore!</span>
      </div>

      <!-- History list -->
      <div v-else class="history-list">
        <div 
          v-for="place in history" 
          :key="place._id"
          class="history-item"
          @click="goToPlace(place)"
        >
          <div class="place-info">
            <div class="place-flag">
              <img 
                v-if="place.countryFlag || place.countryCode"
                :src="place.countryFlag || getFlagUrl(place.countryCode)"
                :alt="`${place.country} flag`"
                class="flag-image"
                @error="handleFlagError"
              />
              <div v-else class="flag-placeholder">🌍</div>
            </div>
            <div class="place-details">
              <h4 class="place-name">{{ place.placeName }}</h4>
              <div class="place-location">
                <i class="fas fa-map-marker-alt"></i>
                <span>
                  <template v-if="place.region && place.country">
                    {{ place.region }}, {{ place.country }}
                  </template>
                  <template v-else-if="place.country">
                    {{ place.country }}
                  </template>
                  <template v-else-if="place.region">
                    {{ place.region }}
                  </template>
                  <template v-else>
                    Unknown location
                  </template>
                </span>
              </div>
              <div class="place-meta">
                <span v-if="isShowingGlobal" class="visit-count">
                  <i class="fas fa-users"></i>
                  {{ place.totalVisits }} visits by {{ place.uniqueVisitors }} {{ place.uniqueVisitors === 1 ? 'explorer' : 'explorers' }}
                </span>
                <span v-else-if="place.visitCount > 1" class="visit-count">
                  <i class="fas fa-eye"></i>
                  {{ place.visitCount }} visits
                </span>
                <span class="last-visited">
                  <i class="fas fa-clock"></i>
                  {{ formatDate(place.lastVisited) }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="!isShowingGlobal" class="place-actions">
            <button 
              @click.stop="removePlace(place._id)"
              class="remove-btn"
              title="Remove from history"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Load more button -->
        <div v-if="pagination.currentPage < pagination.totalPages" class="load-more">
          <button @click="loadMoreHistory" :disabled="loading" class="load-more-btn">
            <i class="fas fa-plus"></i>
            Load More
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Mobile: Drawer Layout -->
  <div v-if="isMobile && isOpen" class="places-drawer-overlay" :class="{ 'drawer-minimized': drawerMinimized }">
    <!-- Minimized Header Bar (always visible) -->
    <div class="drawer-header-bar" @click="expandDrawer" v-if="drawerMinimized">
      <div class="drawer-header-content">
        <i class="fas fa-history drawer-mini-icon"></i>
        <div class="drawer-mini-info">
          <h3 class="drawer-mini-title">{{ isShowingGlobal ? 'Most Visited Places' : 'Places Visited' }}</h3>
          <p class="drawer-mini-location">{{ history.length }} {{ history.length === 1 ? 'place' : 'places' }}</p>
        </div>
        <i class="fas fa-chevron-up drawer-expand-icon"></i>
      </div>
    </div>
    
    <!-- Full Drawer Content -->
    <div class="drawer-content" v-if="!drawerMinimized">
      <!-- Drawer Header with Controls -->
      <div class="drawer-header">
        <div class="drawer-controls">
          <button @click="minimizeDrawer" class="drawer-minimize-btn" aria-label="Minimize">
            <i class="fas fa-chevron-down"></i>
          </button>
          <button @click="closeDrawer" class="drawer-close-btn" aria-label="Close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <!-- Drawer Header Title -->
        <div class="drawer-modal-header">
          <div class="drawer-header-content-full">
            <div class="drawer-title-section">
              <h3 class="drawer-title">
                <i class="fas fa-history"></i>
                {{ isShowingGlobal ? 'Most Visited Places' : 'Places Visited' }}
              </h3>
              <div class="drawer-actions" v-if="hasHistory && !isShowingGlobal">
                <button 
                  @click="clearHistory" 
                  class="drawer-action-btn clear-btn"
                  :disabled="loading"
                  title="Clear all history"
                >
                  <i class="fas fa-trash-alt"></i>
                </button>
                <button 
                  @click="refreshHistory" 
                  class="drawer-action-btn refresh-btn"
                  :disabled="loading"
                  title="Refresh history"
                >
                  <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
                </button>
              </div>
              <div class="drawer-actions" v-else-if="hasHistory && isShowingGlobal">
                <button 
                  @click="refreshGlobal" 
                  class="drawer-action-btn refresh-btn"
                  :disabled="loading"
                  title="Refresh global places"
                >
                  <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Drawer Body -->
      <div class="drawer-body">
        <!-- Loading state -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner">
            <i class="fas fa-spinner fa-spin"></i>
          </div>
          <p>Loading your places...</p>
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="error-state">
          <i class="fas fa-exclamation-triangle"></i>
          <p>{{ error }}</p>
          <button @click="refreshHistory" class="retry-btn">Try Again</button>
        </div>

        <!-- Empty state -->
        <div v-else-if="!hasHistory" class="empty-state">
          <i class="fas fa-map-marker-alt"></i>
          <p v-if="isShowingGlobal">No popular places found</p>
          <p v-else>No places visited yet</p>
          <span v-if="!isShowingGlobal" class="empty-subtitle">Start exploring the world!</span>
          <span v-else class="empty-subtitle">Be the first to explore!</span>
        </div>

        <!-- History list -->
        <div v-else class="drawer-history-list">
          <div 
            v-for="place in history" 
            :key="place._id"
            class="drawer-history-item"
            @click="goToPlace(place)"
          >
            <div class="place-info">
              <div class="place-flag">
                <img 
                  v-if="place.countryFlag || place.countryCode"
                  :src="place.countryFlag || getFlagUrl(place.countryCode)"
                  :alt="`${place.country} flag`"
                  class="flag-image"
                  @error="handleFlagError"
                />
                <div v-else class="flag-placeholder">🌍</div>
              </div>
              <div class="place-details">
                <h4 class="place-name">{{ place.placeName }}</h4>
                <div class="place-location">
                  <i class="fas fa-map-marker-alt"></i>
                  <span>
                    <template v-if="place.region && place.country">
                      {{ place.region }}, {{ place.country }}
                    </template>
                    <template v-else-if="place.country">
                      {{ place.country }}
                    </template>
                    <template v-else-if="place.region">
                      {{ place.region }}
                    </template>
                    <template v-else>
                      Unknown location
                    </template>
                  </span>
                </div>
                <div class="place-meta">
                  <span v-if="isShowingGlobal" class="visit-count">
                    <i class="fas fa-users"></i>
                    {{ place.totalVisits }} visits by {{ place.uniqueVisitors }} {{ place.uniqueVisitors === 1 ? 'explorer' : 'explorers' }}
                  </span>
                  <span v-else-if="place.visitCount > 1" class="visit-count">
                    <i class="fas fa-eye"></i>
                    {{ place.visitCount }} visits
                  </span>
                  <span class="last-visited">
                    <i class="fas fa-clock"></i>
                    {{ formatDate(place.lastVisited) }}
                  </span>
                </div>
              </div>
            </div>
            <div v-if="!isShowingGlobal" class="place-actions">
              <button 
                @click.stop="removePlace(place._id)"
                class="remove-btn"
                title="Remove from history"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>

          <!-- Load more button -->
          <div v-if="pagination.currentPage < pagination.totalPages" class="load-more">
            <button @click="loadMoreHistory" :disabled="loading" class="load-more-btn">
              <i class="fas fa-plus"></i>
              Load More
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'PlacesHistory',
  props: {
    showGlobal: {
      type: Boolean,
      default: false
    },
    isOpen: {
      type: Boolean,
      default: true // Always open for desktop inline mode
    }
  },
  data() {
    return {
      isMobile: window.innerWidth <= 768,
      drawerMinimized: false
    }
  },
  computed: {
    ...mapGetters('placesHistory', ['history', 'loading', 'error', 'pagination', 'hasHistory']),
    ...mapGetters('auth', ['isAuthenticated']),
    
    // Determine which data to show
    isShowingGlobal() {
      return this.showGlobal && !this.isAuthenticated
    }
  },
  async mounted() {
    if (this.isAuthenticated) {
      await this.fetchHistory()
    } else if (this.showGlobal) {
      await this.fetchTopPlaces()
    }

    // Add resize listener for mobile detection
    window.addEventListener('resize', this.checkScreenSize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize)
  },
  watch: {
    isAuthenticated(newVal) {
      if (newVal) {
        this.fetchHistory()
      } else {
        // Clear history when user logs out and fetch global if needed
        this.$store.commit('placesHistory/CLEAR_HISTORY')
        if (this.showGlobal) {
          this.fetchTopPlaces()
        }
      }
    }
  },
  methods: {
    ...mapActions('placesHistory', ['fetchHistory', 'removePlace', 'clearHistory', 'fetchTopPlaces']),

    async refreshHistory() {
      if (this.isShowingGlobal) {
        await this.fetchTopPlaces()
      } else {
        await this.fetchHistory({ page: 1 })
      }
    },

    async refreshGlobal() {
      await this.fetchTopPlaces()
    },

    // Mobile drawer methods
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 768
    },

    minimizeDrawer() {
      this.drawerMinimized = true
    },

    expandDrawer() {
      this.drawerMinimized = false
    },

    closeDrawer() {
      this.$emit('close-drawer')
    },

    async loadMoreHistory() {
      const nextPage = this.pagination.currentPage + 1
      await this.fetchHistory({ page: nextPage })
    },

    goToPlace(place) {
      // Close drawer on mobile when navigating to place
      if (this.isMobile && this.isOpen && !this.drawerMinimized) {
        this.closeDrawer()
      }
      
      // Emit event to parent component to navigate to place
      this.$emit('navigate-to-place', {
        coordinates: {
          latitude: place.coordinates.latitude,
          longitude: place.coordinates.longitude
        },
        placeName: place.placeName
      })
    },

    async removePlace(placeId) {
      if (confirm('Are you sure you want to remove this place from your history?')) {
        await this.removePlace(placeId)
      }
    },

    async clearHistory() {
      if (confirm('Are you sure you want to clear all your place history? This action cannot be undone.')) {
        await this.clearHistory()
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString)
      const now = new Date()
      const diffInHours = (now - date) / (1000 * 60 * 60)

      if (diffInHours < 1) {
        return 'Just now'
      } else if (diffInHours < 24) {
        return `${Math.floor(diffInHours)}h ago`
      } else if (diffInHours < 24 * 7) {
        return `${Math.floor(diffInHours / 24)}d ago`
      } else {
        return date.toLocaleDateString()
      }
    },
    
    getFlagUrl(countryCode) {
      if (!countryCode) return null
      return `https://flagpedia.net/data/flags/w580/${countryCode.toLowerCase()}.webp?v=un`
    },
    
    handleFlagError(event) {
      // Fallback for missing flags - replace with placeholder
      const placeholder = document.createElement('div')
      placeholder.className = 'flag-placeholder'
      placeholder.textContent = '🌍'
      event.target.parentNode.replaceChild(placeholder, event.target)
    }
  }
}
</script>

<style scoped>
.places-history {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.history-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
}

.history-title i {
  color: #6366f1;
}

.history-actions {
  display: flex;
  gap: 8px;
}

.clear-btn, .refresh-btn {
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s ease;
}

.clear-btn:hover {
  background: #fef2f2;
  border-color: #fca5a5;
  color: #dc2626;
}

.refresh-btn:hover {
  background: #eff6ff;
  border-color: #93c5fd;
  color: #2563eb;
}

.clear-btn:disabled, .refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.history-content {
  flex: 1;
  overflow-y: auto;
}

.loading-state, .error-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
}

.loading-spinner i {
  font-size: 24px;
  color: #6366f1;
  margin-bottom: 12px;
}

.error-state i {
  font-size: 24px;
  color: #dc2626;
  margin-bottom: 12px;
}

.empty-state i {
  font-size: 32px;
  color: #9ca3af;
  margin-bottom: 12px;
}

.empty-subtitle {
  font-size: 14px;
  color: #9ca3af;
}

.retry-btn {
  margin-top: 12px;
  padding: 8px 16px;
  background: #6366f1;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s ease;
}

.retry-btn:hover {
  background: #5856eb;
}

.history-list {
  padding: 8px;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 4px;
}

.history-item:hover {
  background: #f1f5f9;
}

.place-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.place-flag {
  width: 32px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  overflow: hidden;
}

.flag-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 2px;
}

.flag-placeholder {
  font-size: 16px;
  text-align: center;
}

.place-details {
  flex: 1;
  min-width: 0;
}

.place-name {
  margin: 0 0 2px 0;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.place-location {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 2px;
}

.place-location i {
  font-size: 10px;
  width: 10px;
  color: #9ca3af;
}

.place-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: #6b7280;
}

.visit-count, .last-visited {
  display: flex;
  align-items: center;
  gap: 4px;
}

.place-actions {
  display: flex;
  gap: 4px;
}

.remove-btn {
  background: none;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s ease;
  opacity: 0;
}

.history-item:hover .remove-btn {
  opacity: 1;
}

.remove-btn:hover {
  background: #fef2f2;
  color: #dc2626;
}

.load-more {
  padding: 16px;
  text-align: center;
}

.load-more-btn {
  padding: 8px 16px;
  background: #f8fafc;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 auto;
}

.load-more-btn:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
}

.load-more-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .history-header {
    padding: 12px;
  }
  
  .history-title {
    font-size: 14px;
  }
  
  .place-flag {
    width: 28px;
    height: 20px;
  }
  
  .place-name {
    font-size: 13px;
  }
  
  .place-location {
    font-size: 11px;
  }
  
  .place-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    font-size: 10px;
  }
  
  .visit-count, .last-visited {
    font-size: 10px;
  }
}

/* Mobile Drawer Styles */
.places-drawer-overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999999;
  background: white;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.12);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 80vh;
  animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.places-drawer-overlay.drawer-minimized {
  transform: translateY(calc(100% - 70px));
}

.drawer-header-bar {
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  background: white;
  border-radius: 16px 16px 0 0;
}

.drawer-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.drawer-mini-icon {
  font-size: 20px;
  color: #667eea;
}

.drawer-mini-info {
  flex: 1;
}

.drawer-mini-title {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 2px 0;
}

.drawer-mini-location {
  font-size: 12px;
  color: #6b7280;
  margin: 0;
}

.drawer-expand-icon {
  font-size: 14px;
  color: #9ca3af;
}

.drawer-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 80vh;
}

.drawer-header {
  position: sticky;
  top: 0;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  z-index: 10;
  border-radius: 16px 16px 0 0;
}

.drawer-controls {
  display: flex;
  justify-content: space-between;
  padding: 16px 20px 8px;
}

.drawer-minimize-btn,
.drawer-close-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.drawer-minimize-btn {
  background: #f3f4f6;
  color: #6b7280;
}

.drawer-minimize-btn:hover {
  background: #e5e7eb;
  color: #374151;
}

.drawer-close-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.drawer-close-btn:hover {
  background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
  transform: scale(1.05);
}

.drawer-modal-header {
  padding: 0 20px 16px;
}

.drawer-title-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.drawer-title {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.drawer-title i {
  color: #667eea;
  font-size: 16px;
}

.drawer-actions {
  display: flex;
  gap: 8px;
}

.drawer-action-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.drawer-action-btn:hover {
  border-color: #d1d5db;
  background: #f9fafb;
}

.drawer-action-btn.clear-btn {
  color: #dc2626;
}

.drawer-action-btn.clear-btn:hover {
  background: #fef2f2;
  border-color: #fca5a5;
}

.drawer-action-btn.refresh-btn {
  color: #2563eb;
}

.drawer-action-btn.refresh-btn:hover {
  background: #eff6ff;
  border-color: #93c5fd;
}

.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 20px 20px;
}

.drawer-history-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.drawer-history-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
}

.drawer-history-item:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transform: translateY(-1px);
}

.drawer-history-item:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

@keyframes slideUp {
  0% {
    transform: translateY(100%);
  }
  100% {
    transform: translateY(0);
  }
}
</style>